// Inline SVG icon set, ported from the spec (24×24, stroke: currentColor).
// The spec ships no image assets — every glyph is inline, which keeps the CSP
// strict and the icons themeable.

export type IconName =
  | 'person'
  | 'gear'
  | 'barcode'
  | 'search'
  | 'bell'
  | 'back'
  | 'play'
  | 'file'
  | 'undo'
  | 'warn'
  | 'hazard'
  | 'logout'
  | 'camera'
  | 'ruler'
  | 'list'
  | 'check'
  | 'cross'
  | 'mic'
  // The tab bar and the raise sheet (frames 3b, 1f).
  | 'home'
  | 'pod'
  | 'more'
  | 'grid'
  | 'shield'
  | 'clock'
  | 'chevron'
  | 'external'
  | 'checkCircle'
  // A subfolder of a pod's drawings (the Plans & documents sheet).
  | 'folder'
  // The floating + at the bottom-right of a phone (frame v2.12).
  | 'plus'
  // The raise flow's tiles and its answer box, lifted from the Andon app's
  // icon set so the tiles read the same on both apps while they coexist.
  | 'box'
  | 'wrench'
  | 'info'
  | 'drawing'
  | 'send'
  // The andon thread (step 4 of the merge): the composer's picker rows, the
  // react key, the verbs, the video recorder's controls, the close sheet's
  // stepper. Lifted from the Andon app's set so the two read alike.
  | 'handover'
  | 'escalate'
  | 'caret'
  | 'video'
  | 'keyboard'
  | 'gif'
  | 'upload'
  | 'gallery'
  | 'smile'
  | 'torch'
  | 'cameraFlip';

const PATHS: Record<IconName, { d: string[]; w?: number; extra?: React.ReactNode }> = {
  person: { d: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z', 'M4.5 20a7.5 7.5 0 0 1 15 0'] },
  gear: {
    d: [
      'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
      'M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5v.2a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H2a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 3.6 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H8a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1h.2a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z',
    ],
  },
  barcode: { d: ['M4 5v14', 'M8 5v14', 'M12 5v14', 'M16 5v14', 'M20 5v14'], w: 2 },
  search: { d: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z', 'm21 21-4.3-4.3'] },
  bell: { d: ['M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9', 'M13.7 21a2 2 0 0 1-3.4 0'] },
  back: { d: ['m15 18-6-6 6-6'], w: 2.2 },
  play: { d: ['M8 5.5v13l11-6.5-11-6.5Z'] },
  file: { d: ['M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z', 'M14 2v6h6'] },
  undo: { d: ['M3 7v6h6', 'M3.5 13a9 9 0 1 0 2.1-6.4L3 9'] },
  warn: { d: ['M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z', 'M12 9v4', 'M12 17h.01'] },
  hazard: { d: ['M12 2 1 21h22L12 2Z', 'M12 9v5', 'M12 18h.01'] },
  logout: { d: ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'm16 17 5-5-5-5', 'M21 12H9'] },
  camera: {
    d: [
      'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2v11Z',
      'M12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z',
    ],
  },
  ruler: { d: ['M2 9h20v6H2z', 'M6 9v3', 'M10 9v4', 'M14 9v3', 'M18 9v4'] },
  list: { d: ['M8 6h13', 'M8 12h13', 'M8 18h13', 'M3 6h.01', 'M3 12h.01', 'M3 18h.01'] },
  check: { d: ['m4 12 6 6L20 6'], w: 3 },
  cross: { d: ['M6 6l12 12', 'M18 6 6 18'], w: 3 },
  mic: { d: ['M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z', 'M19 11a7 7 0 0 1-14 0', 'M12 18v3'] },
  home: { d: ['M3 11l9-8 9 8v9a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2z'], w: 2 },
  // A box seen in three-quarter view: the pods tab, and the closest thing to a
  // pod the icon grammar has that is not the brand mark.
  pod: { d: ['M21 8l-9-5-9 5v8l9 5 9-5z', 'M3 8l9 5 9-5M12 13v8'], w: 2 },
  more: { d: [], w: 0, extra: (
    <>
      <circle cx="5" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="2" fill="currentColor" stroke="none" />
    </>
  ) },
  grid: { d: [], w: 2, extra: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="2" />
      <rect x="13" y="3" width="8" height="8" rx="2" />
      <rect x="3" y="13" width="8" height="8" rx="2" />
      <rect x="13" y="13" width="8" height="8" rx="2" />
    </>
  ) },
  shield: { d: ['M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z'], w: 2 },
  clock: { d: ['M12 8v4l2.5 2.5'], w: 2, extra: <circle cx="12" cy="12" r="9" /> },
  checkCircle: { d: ['M8.5 12.5l2.5 2.5 4.5-5'], w: 2, extra: <circle cx="12" cy="12" r="9" /> },
  chevron: { d: ['m9 6 6 6-6 6'], w: 2 },
  // Marks a control that leaves the app — the andon rows link out to the
  // Andon app, and a tap that changes screen entirely should say so.
  external: { d: ['M14 4h6v6', 'M20 4l-9 9', 'M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5'], w: 2 },
  folder: { d: ['M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z'] },
  plus: { d: ['M12 5v14', 'M5 12h14'], w: 2.2 },
  box: { d: ['M21 8v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8', 'M2 4h20v4H2z', 'M10 12h4'] },
  wrench: { d: ['M14.7 6.3a4 4 0 0 1 5 5L21 21H3l7.3-7.3a4 4 0 0 1 4.4-7.4Z'] },
  info: { d: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M12 8h.01', 'M11 12h1v5'] },
  drawing: { d: ['M4 4h16v16H4z', 'M4 10h16', 'M10 10v10'] },
  send: { d: ['M22 2 11 13', 'M22 2l-7 20-4-9-9-4 20-7Z'] },
  handover: { d: ['M16 3h5v5', 'M21 3l-9 9', 'M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5'] },
  escalate: { d: ['M12 20V5', 'm5 12 7-7 7 7'], w: 2 },
  caret: { d: ['m6 9 6 6 6-6'], w: 2.4 },
  video: { d: ['M3 6h11a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z', 'm16 11 5-3v8l-5-3'] },
  keyboard: { d: ['M4 6h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z', 'M7 10h.01', 'M11 10h.01', 'M15 10h.01', 'M8 14h8'], w: 1.8 },
  gif: { d: ['M4 4h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z', 'M9 10a2 2 0 1 0-2 2 2 2 0 0 0 2-2Z', 'M12 15s1.2 1.5 3 1.5S18 15 18 15'] },
  upload: { d: ['M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3', 'M12 15V3', 'm8 7 4-4 4 4'], w: 1.8 },
  gallery: { d: ['M4 5h16a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1Z', 'M8.5 10.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z', 'm3 16 5-4 4 3 3-2 6 5'], w: 1.8 },
  smile: { d: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', 'M9 10h.01', 'M15 10h.01', 'M8.5 14.5a4.5 4.5 0 0 0 7 0'], w: 1.8 },
  // A torch, pointing down the way a hand holds one: the barrel above, the
  // beam widening below it. NOT a sun with a different label — a sun means
  // brightness, and this control means "switch the lamp on", which is a
  // different promise on a phone whose lamp may not exist.
  torch: { d: ['M9 2h6v4l-1.5 3h-3L9 6V2Z', 'M10.5 9v4', 'M13.5 9v4', 'M12 16v5', 'M8 19l-1.5 3', 'M16 19l1.5 3'], w: 1.8 },
  // Flip the lens: a camera body with two arrows curling round it. The arrows
  // are what carry the meaning — a bare camera outline already means "take a
  // photo" everywhere else in this app.
  cameraFlip: {
    d: ['M12 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z', 'M20 12.5V17a2 2 0 0 1-2 2h-3', 'm17 16-2 3 3 2', 'M4 11.5V7a2 2 0 0 1 2-2h3', 'm7 8 2-3-3-2'],
    w: 1.8,
  },
};

export interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  style?: React.CSSProperties;
}

export function Icon({ name, size = 20, strokeWidth, style }: IconProps) {
  const spec = PATHS[name];
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth ?? spec.w ?? 1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      style={{ flex: 'none', ...style }}
    >
      {spec.d.map((d, i) => (
        <path key={i} d={d} />
      ))}
      {spec.extra}
    </svg>
  );
}

/** The Interpod pod mark: a disc with a rounded-square hole. */
export function PodMark({ size = 26, style }: { size?: number; style?: React.CSSProperties }) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      fill="currentColor"
      fillRule="evenodd"
      aria-hidden="true"
      focusable="false"
      style={{ flex: 'none', ...style }}
    >
      <path d="M2 50 A48 48 0 1 1 98 50 A48 48 0 1 1 2 50 Z M42 30 H58 A12 12 0 0 1 70 42 V58 A12 12 0 0 1 58 70 H42 A12 12 0 0 1 30 58 V42 A12 12 0 0 1 42 30 Z" />
    </svg>
  );
}

/** Language flags (20×14), inline per the spec. */
export function Flag({ lang, style }: { lang: 'AU' | '中文' | 'Fil'; style?: React.CSSProperties }) {
  const common = { width: 20, height: 14, style: { borderRadius: 2, flex: 'none', ...style } } as const;
  if (lang === '中文') {
    return (
      <svg viewBox="0 0 20 14" {...common} aria-hidden="true">
        <rect width="20" height="14" fill="#de2910" />
        <circle cx="5" cy="4.5" r="2.2" fill="#ffde00" />
      </svg>
    );
  }
  if (lang === 'Fil') {
    return (
      <svg viewBox="0 0 20 14" {...common} aria-hidden="true">
        <rect width="20" height="7" fill="#0038a8" />
        <rect y="7" width="20" height="7" fill="#ce1126" />
        <path d="M0 0 L8 7 L0 14 Z" fill="#fff" />
        <circle cx="2.6" cy="7" r="1.5" fill="#fcd116" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 20 14" {...common} aria-hidden="true">
      <rect width="20" height="14" fill="#00247d" />
      <path d="M0 0 L9 6 M9 0 L0 6" stroke="#fff" strokeWidth="1.4" />
      <path d="M0 3 h9 M4.5 0 v6" stroke="#fff" strokeWidth="2" />
      <path d="M0 3 h9 M4.5 0 v6" stroke="#cf142b" strokeWidth="1" />
      <circle cx="14.5" cy="9" r="1.6" fill="#fff" />
    </svg>
  );
}
